import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Page, Layout, BlockStack, Banner, Text } from '@shopify/polaris';
import SearchPane from './SearchPane';
import SkuStackPane from './SkuStackPane';
import CalendarPane from './CalendarPane';
import { api } from './apiClient';

function keyOf(article, color) {
  return `${article}|${color}`;
}

export default function AdidasMap() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();

  // History stack of viewed SKUs. New search results prepend; clicking an
  // existing card does NOT reorder the stack — it only changes which SKU is
  // active (drives the calendar). Seeded from URL for shareability.
  const [stack, setStack] = useState(() => {
    const a = params.get('article');
    const c = params.get('color');
    return a && c ? [{ article: a, color: c }] : [];
  });

  // The active SKU is tracked independently of stack order so clicking a card
  // can highlight it without promoting it to the top.
  const [activeKey, setActiveKey] = useState(() => {
    const a = params.get('article');
    const c = params.get('color');
    return a && c ? keyOf(a, c) : null;
  });

  // Cache of fetched SkuDetails keyed by article|color so re-adding doesn't
  // re-fetch and so CalendarPane can re-render synchronously.
  const [details, setDetails] = useState({});

  // Anchor color of the active SKU's bars (the base/MAP color). CalendarPane
  // computes it from the SkuDetail and passes it back up so the active card
  // can be tinted the same hue.
  const [anchorColor, setAnchorColor] = useState(null);

  // Which Adidas file is live and how old it is. Adidas updates MAP monthly, so an old file gets a warning.
  const [status, setStatus] = useState(null);
  useEffect(() => { api.status().then(setStatus).catch(() => setStatus(null)); }, []);
  const seasonLabel = status?.season ? `${status.season.replace(/^(FW|SS)(20)?(\d\d)$/, '$1$3')} MAP` : 'MAP';

  // Fetch any newly-stacked SKUs we haven't cached yet.
  useEffect(() => {
    stack.forEach(({ article, color }) => {
      const k = keyOf(article, color);
      if (!(k in details)) {
        api.sku(article, color)
          .then((d) => setDetails((prev) => ({ ...prev, [k]: d })))
          .catch(() => { /* swallow — card will keep spinner */ });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stack]);

  // Active SKU drives the URL for shareability.
  useEffect(() => {
    if (activeKey) {
      const [a, c] = activeKey.split('|');
      if (params.get('article') !== a || params.get('color') !== c) {
        setParams({ article: a, color: c });
      }
    } else if (params.get('article') || params.get('color')) {
      setParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  function onSelect(hit) {
    const k = keyOf(hit.article, hit.color);
    setStack((prev) => {
      const filtered = prev.filter((s) => !(s.article === hit.article && s.color === hit.color));
      return [{ article: hit.article, color: hit.color }, ...filtered];
    });
    setActiveKey(k);
  }

  function onActivate(article, color) {
    setActiveKey(keyOf(article, color));
  }

  function onRemove(article, color) {
    const k = keyOf(article, color);
    setStack((prev) => {
      const next = prev.filter((s) => !(s.article === article && s.color === color));
      // If we just removed the active SKU, fall back to whichever card is
      // visually at the top of the (now-shrunken) stack.
      if (activeKey === k) {
        const fallback = next[0];
        setActiveKey(fallback ? keyOf(fallback.article, fallback.color) : null);
      }
      return next;
    });
  }

  const activeRef = activeKey
    ? (() => {
        const [a, c] = activeKey.split('|');
        return { article: a, color: c };
      })()
    : undefined;
  const activeSku = activeRef ? details[keyOf(activeRef.article, activeRef.color)] : undefined;

  // Stable identity so CalendarPane's effect doesn't re-fire on every render.
  const handleAnchorColorChange = useCallback((hex) => {
    setAnchorColor(hex);
  }, []);

  return (
    <Page
      title={`Adidas ${seasonLabel}`}
      primaryAction={{ content: 'Upload new file', onAction: () => nav('/adidas-map/upload') }}
      secondaryActions={[
        { content: 'Upload history', onAction: () => nav('/adidas-map/uploads') },
      ]}
    >
      <Layout>
        {status && (
          <Layout.Section variant="fullWidth">
            <Banner tone={status.file_age?.stale ? 'warning' : 'info'}>
              <Text as="p">
                {status.current_upload_id
                  ? `Live data: ${status.filename_xlsm}, applied ${status.applied_at ? new Date(status.applied_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}${status.applied_by_name ? ` by ${status.applied_by_name}` : ''}${status.file_age?.file_date ? `; Adidas report dated ${new Date(status.file_age.file_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}${status.file_age?.days != null ? ` (${status.file_age.days} days old)` : ''}. `
                  : 'No Adidas file has been applied yet. '}
                {status.file_age?.stale ? 'Adidas updates MAP monthly, so a newer file is probably available: upload it before relying on these prices. ' : 'Adidas updates MAP monthly; upload each new file when it arrives. '}
                The spreadsheet is the source of truth; the policy document is kept for reference only. Windows switch at midnight Eastern here, while Adidas&apos;s reference clock is Pacific.
              </Text>
            </Banner>
          </Layout.Section>
        )}
        <Layout.Section variant="oneThird">
          <BlockStack gap="300">
            <SearchPane onSelect={onSelect} />
            <div className="sku-stack-scroll">
              <SkuStackPane
                stack={stack}
                details={details}
                onRemove={onRemove}
                onActivate={onActivate}
                activeArticle={activeRef?.article}
                activeColor={activeRef?.color}
                activeSkuColor={anchorColor}
              />
            </div>
          </BlockStack>
        </Layout.Section>
        <Layout.Section>
          <CalendarPane sku={activeSku} onAnchorColorChange={handleAnchorColorChange} seasonLabel={seasonLabel} />
        </Layout.Section>
      </Layout>
    </Page>
  );
}
